import { useState } from 'react'
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import clsx from 'clsx'
import { Button } from '@/components/common/Button'
import { isFlaggedPlan, isOutageWaivable, needsOutageAck, type ActionGate } from '@/utils/patchStage'
import { MAX_WINDOW_MINUTES, MAX_WINDOW_START_DAYS, buildMaintenanceWindow } from '@/utils/patchValidation'
import type { ApproveStageBody, PatchCampaign, PatchStage } from '@/types/patching'
import styles from './Patching.module.css'

export interface ActionResult {
  ok: boolean
  /** HTTP status of the failure, when there was one. */
  status: number | null
}

interface ApprovalFormProps {
  campaign: PatchCampaign
  stage: PatchStage
  approve: ActionGate
  reject: ActionGate
  busy: boolean
  approving: boolean
  rejecting: boolean
  selfRequested: boolean
  onApprove: (body: ApproveStageBody) => Promise<ActionResult>
  onReject: (notes: string) => Promise<ActionResult>
}

function shortHash(hash: string | null | undefined): string {
  return hash ? hash.slice(0, 12) : 'none'
}

/**
 * Approve / Reject (contract §9, §11.4). The approval is bound to the plan and scope hashes that are
 * on screen right now; if either changed the portal answers 409 and the acknowledgements are reset
 * so the approver has to look again. The maintenance window is set here, by the approver, for prod.
 */
export function ApprovalForm({
  campaign,
  stage,
  approve,
  reject,
  busy,
  approving,
  rejecting,
  selfRequested,
  onApprove,
  onReject,
}: ApprovalFormProps) {
  const env = stage.environment
  const [notes, setNotes] = useState('')
  const [ackReview, setAckReview] = useState(false)
  const [ackOutage, setAckOutage] = useState(false)
  const [windowStart, setWindowStart] = useState('')
  const [windowEnd, setWindowEnd] = useState('')
  const [windowError, setWindowError] = useState<string | null>(null)

  // A destructive plan cannot be approved at all (§11.6), so only the other flagged plans ask for an acknowledgement.
  const flagged = isFlaggedPlan(stage.plan) && !stage.plan?.destructive
  const outageNeeded = needsOutageAck(stage)
  const windowNeeded = env === 'prod'
  const planHash = stage.plan?.plan_hash ?? null
  const outageReasons = (stage.risks ?? []).filter(isOutageWaivable)

  const approveDisabled = busy || approve.disabledReason !== null || (flagged && !ackReview) || (outageNeeded && !ackOutage)
  const approveTitle =
    approve.disabledReason ??
    (flagged && !ackReview
      ? 'Acknowledge the additional review first'
      : outageNeeded && !ackOutage
        ? 'Acknowledge the outage first'
        : undefined)

  async function handleApprove() {
    const body: ApproveStageBody = { notes, plan_hash: planHash, scope_hash: campaign.scope_hash }
    if (windowNeeded) {
      const built = buildMaintenanceWindow(windowStart, windowEnd)
      if (built.error !== null) {
        setWindowError(built.error)
        return
      }
      body.maintenance_window = built.window
    }
    setWindowError(null)
    if (flagged) body.acknowledge_additional_review = true
    if (outageNeeded && ackOutage) body.acknowledge_outage = true
    const result = await onApprove(body)
    // 409 = the plan or scope changed under the approver: earlier acknowledgements no longer apply.
    if (result.status === 409) {
      setAckReview(false)
      setAckOutage(false)
    }
  }

  return (
    <div className={styles.actionBlock} data-testid="approval-form">
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`notes-${env}`}>Notes</label>
        <textarea
          id={`notes-${env}`}
          className={styles.textarea}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
          placeholder="Reason / context for the decision (recorded in the audit trail)"
        />
      </div>
      {selfRequested && (
        <span className={styles.hint} data-testid="self-request-note">
          You requested this campaign. The portal enforces separation of duties and may reject your approval.
        </span>
      )}

      {approve.visible && (
        <>
          <span className={styles.hint} data-testid="approve-hashes">
            Your approval binds to plan <span className={styles.mono}>{shortHash(planHash)}</span> and scope{' '}
            <span className={styles.mono}>{shortHash(campaign.scope_hash)}</span>. If either changes, the portal refuses it.
          </span>

          {windowNeeded && (
            <fieldset className={styles.stack} style={{ border: '1px solid var(--aptean-border)', borderRadius: 8, padding: 12, margin: 0, gap: 10 }}>
              <legend className={styles.label}>Maintenance window (required for PROD)</legend>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="mw-start">Window start (your local time)</label>
                <input
                  id="mw-start"
                  type="datetime-local"
                  className={clsx(styles.input, windowError && styles.inputError)}
                  value={windowStart}
                  onChange={(e) => setWindowStart(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="mw-end">Window end (your local time)</label>
                <input
                  id="mw-end"
                  type="datetime-local"
                  className={clsx(styles.input, windowError && styles.inputError)}
                  value={windowEnd}
                  onChange={(e) => setWindowEnd(e.target.value)}
                />
              </div>
              <span className={styles.hint}>
                Sent to the portal as UTC. At most {MAX_WINDOW_MINUTES} minutes, ending in the future and starting within{' '}
                {MAX_WINDOW_START_DAYS} days. Execution is only allowed inside this window.
              </span>
              {windowError && (
                <ul className={styles.fieldError} role="alert" data-testid="window-error">
                  <li>{windowError}</li>
                </ul>
              )}
            </fieldset>
          )}

          {outageNeeded && (
            <div className={styles.stack} style={{ gap: 6 }} data-testid="outage-ack-block">
              <div className={clsx(styles.alert, styles.alertWarning)}>
                <AlertTriangle size={16} className={styles.alertIcon} />
                <div>
                  The risk policy needs an outage acknowledgement before this can proceed:
                  <ul className={styles.advisorList}>
                    {outageReasons.map((risk) => (
                      <li key={risk.vm_name}>
                        <span className={styles.mono}>{risk.vm_name}</span>
                        {risk.reasons.length > 0 ? ` — ${risk.reasons.join('; ')}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <label className={styles.checkboxRow}>
                <input type="checkbox" checked={ackOutage} onChange={(e) => setAckOutage(e.target.checked)} data-testid="ack-outage" />
                <span>I acknowledge the outage. The VMs above will be unavailable while they are patched and rebooted.</span>
              </label>
            </div>
          )}

          {flagged && (
            <label className={styles.checkboxRow}>
              <input type="checkbox" checked={ackReview} onChange={(e) => setAckReview(e.target.checked)} data-testid="ack-additional-review" />
              <span>
                I have reviewed this destructive / scope-expanding plan and acknowledge the additional review requirement.
              </span>
            </label>
          )}
        </>
      )}

      <div className={styles.actionRow}>
        {approve.visible && (
          <Button icon={<CheckCircle2 size={14} />} onClick={handleApprove} loading={approving} disabled={approveDisabled} title={approveTitle}>
            Approve
          </Button>
        )}
        {reject.visible && (
          <Button variant="danger" icon={<XCircle size={14} />} onClick={() => onReject(notes)} loading={rejecting} disabled={busy}>
            Reject
          </Button>
        )}
      </div>
      {approve.disabledReason && <span className={styles.hint}>{approve.disabledReason}</span>}
      {approve.visible && flagged && !ackReview && (
        <span className={styles.hint}>Approval is disabled until the additional-review acknowledgement is ticked.</span>
      )}
      {approve.visible && outageNeeded && !ackOutage && (
        <span className={styles.hint}>Approval is disabled until the outage is acknowledged.</span>
      )}
    </div>
  )
}
